import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  Boxes,
  History,
  LayoutDashboard,
  LogOut,
  Package,
  RefreshCw,
  Settings as SettingsIcon,
  Store,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { AmLogo } from "@/components/AmLogo";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { RefreshTone } from "@/components/RefreshNowButton";

interface NavItem {
  to: string;
  label: string;
  /** Etichetta corta sotto l'icona del menu stretto. */
  shortLabel?: string;
  icon: LucideIcon;
  badge?: string | number | null;
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: "/", label: "Panoramica", icon: LayoutDashboard, end: true, badge: null },
  { to: "/prodotti", label: "Prodotti", icon: Package, badge: null },
  { to: "/container", label: "Container", icon: Boxes, badge: null },
  { to: "/marketplaces", label: "Marketplaces", shortLabel: "Canali", icon: Store, badge: null },
  { to: "/approvals", label: "Da approvare", shortLabel: "Approva", icon: Bell, badge: null },
  { to: "/history", label: "Storico", icon: History, badge: null },
  { to: "/settings", label: "Impostazioni", shortLabel: "Imposta", icon: SettingsIcon, badge: null },
];

const APPROVALS_PATH = "/approvals";

type RefreshSocketEvent = {
  kind: string;
  payload?: Record<string, unknown>;
  occurred_at?: string;
};

type RefreshRunState = {
  isBusy: boolean;
  tone: RefreshTone;
  statusLabel: string | null;
  detailLabel: string | null;
  expectedCount: number | null;
  completedMarketplaces: string[];
  failedMarketplaces: string[];
  missingMarketplaces: string[];
  // marketplace_id attualmente in fetch P11 (per pallino azzurro nella barra
  // sync). Si setta su `refresh_marketplace_started` e ai heartbeat;
  // si azzera quando arriva refresh_completed/_failed per quel mp.
  currentMarketplaceId: string | null;
  updatedAt: string | null;
};

export interface LayoutOutletContext {
  refreshControl: {
    trigger: () => void;
    isBusy: boolean;
    buttonLabel: string;
    statusLabel: string | null;
    detailLabel: string | null;
    tone: RefreshTone;
  };
}

const INITIAL_REFRESH_RUN: RefreshRunState = {
  isBusy: false,
  tone: "running",
  statusLabel: null,
  detailLabel: null,
  expectedCount: null,
  completedMarketplaces: [],
  failedMarketplaces: [],
  missingMarketplaces: [],
  currentMarketplaceId: null,
  updatedAt: null,
};

type CatalogSyncPhase = "running" | "completed" | "failed";

type CatalogSyncState = {
  phase: CatalogSyncPhase | null;
  processed: number;
  inserted: number;
  updated: number;
  pages: number;
  totalEstimate: number | null;
  percent: number;
  message: string | null;
  // Timer per nascondere il banner dopo `completed/failed` (4-5s).
  hideAt: number | null;
};

const INITIAL_CATALOG_SYNC: CatalogSyncState = {
  phase: null,
  processed: 0,
  inserted: 0,
  updated: 0,
  pages: 0,
  totalEstimate: null,
  percent: 0,
  message: null,
  hideAt: null,
};

export function Layout(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const meQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: api.me,
    staleTime: 5 * 60_000,
  });
  const logout = useMutation({
    mutationFn: () => api.logout(),
    onSettled: () => {
      queryClient.clear();
      navigate("/login", { replace: true });
    },
  });
  const initials =
    (meQuery.data?.display_name ?? "")
      .split(" ")
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "··";
  const marketplaces = useQuery({ queryKey: ["marketplaces"], queryFn: api.marketplaces });
  const enabledMarketplaceIds = useMemo(
    () => (marketplaces.data ?? []).filter((marketplace) => marketplace.enabled).map((m) => m.id),
    [marketplaces.data],
  );
  // Keep latest count reachable from the WS callback without forcing the
  // effect to re-run (which would tear down the socket and drop in-flight events).
  const enabledCountRef = useRef<number>(enabledMarketplaceIds.length);
  const marketplaceLabelRef = useRef<Record<string, string>>({});
  useEffect(() => {
    enabledCountRef.current = enabledMarketplaceIds.length;
  }, [enabledMarketplaceIds.length]);
  useEffect(() => {
    marketplaceLabelRef.current = Object.fromEntries(
      (marketplaces.data ?? []).map((marketplace) => [marketplace.id, marketplace.label]),
    );
  }, [marketplaces.data]);
  const [refreshRun, setRefreshRun] = useState<RefreshRunState>(INITIAL_REFRESH_RUN);
  const [catalogSync, setCatalogSync] = useState<CatalogSyncState>(INITIAL_CATALOG_SYNC);

  // Auto-hide del banner catalog sync dopo completed/failed.
  // hideAt e' un timestamp ms; quando current.now > hideAt -> reset.
  useEffect(() => {
    if (catalogSync.hideAt === null) return;
    const delay = Math.max(0, catalogSync.hideAt - Date.now());
    const timer = window.setTimeout(() => {
      setCatalogSync(INITIAL_CATALOG_SYNC);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [catalogSync.hideAt]);

  const refresh = useMutation({
    mutationFn: () => api.refreshNow(),
    onMutate: () => {
      setRefreshRun(startRefreshRun(enabledMarketplaceIds.length || null));
    },
    onError: (error) => {
      setRefreshRun({
        ...INITIAL_REFRESH_RUN,
        tone: "error",
        statusLabel: "Richiesta non inviata",
        detailLabel: error instanceof Error ? error.message : String(error),
        updatedAt: new Date().toISOString(),
      });
    },
  });

  // Badge "Da approvare": contatore alert non visti, alimentato dal WS
  // (reprice_alert) e azzerato quando si apre la pagina. Niente polling.
  const location = useLocation();
  const [unseenAlerts, setUnseenAlerts] = useState(0);
  // Toast effimero sull'ultimo alert ricevuto (oggetto nuovo ad ogni evento
  // cosi' l'effetto di auto-dismiss si ri-arma anche per lo stesso SKU).
  const [alertToast, setAlertToast] = useState<{ sku: string } | null>(null);

  useEffect(() => {
    if (location.pathname === APPROVALS_PATH) setUnseenAlerts(0);
  }, [location.pathname]);

  useEffect(() => {
    if (alertToast === null) return;
    const timer = window.setTimeout(() => setAlertToast(null), 6000);
    return () => window.clearTimeout(timer);
  }, [alertToast]);

  useEffect(() => {
    let socket: WebSocket | null = null;
    let retryTimer: number | null = null;
    let disposed = false;

    const connect = () => {
      if (disposed) return;
      socket = new WebSocket(getEventsSocketUrl());
      socket.onmessage = (message) => {
        const event = parseSocketEvent(message.data);
        if (!event) return;

        if (event.kind === "reprice_alert") {
          const sku = asString(event.payload?.sku);
          setUnseenAlerts((count) => count + 1);
          setAlertToast({ sku: sku ?? "" });
          queryClient.invalidateQueries({ queryKey: ["approvals"] });
          return;
        }

        if (event.kind === "refresh_job_started") {
          const runnable = asStringArray(event.payload?.runnable_marketplaces);
          setRefreshRun(startRefreshRun(runnable.length || enabledCountRef.current || null));
          return;
        }

        if (event.kind === "refresh_marketplace_started") {
          const marketplaceId = asString(event.payload?.marketplace);
          const productsSeen = asNumber(event.payload?.products_seen);
          setRefreshRun((current) => ({
            ...current,
            isBusy: true,
            tone: "running",
            statusLabel: "Aggiornamento in corso",
            detailLabel: marketplaceProgressDetail(
              marketplaceId,
              productsSeen,
              marketplaceLabelRef.current,
            ),
            currentMarketplaceId: marketplaceId ?? current.currentMarketplaceId,
            updatedAt: event.occurred_at ?? new Date().toISOString(),
          }));
          return;
        }

        if (event.kind === "refresh_heartbeat") {
          const marketplaceId = asString(event.payload?.marketplace);
          const eans = asNumber(event.payload?.eans);
          setRefreshRun((current) => ({
            ...current,
            isBusy: true,
            tone: "running",
            statusLabel: "Aggiornamento in corso",
            detailLabel: heartbeatDetail(marketplaceId, eans, marketplaceLabelRef.current),
            currentMarketplaceId: marketplaceId ?? current.currentMarketplaceId,
            updatedAt: event.occurred_at ?? new Date().toISOString(),
          }));
          return;
        }

        if (event.kind === "refresh_partial") {
          const missing = asStringArray(event.payload?.missing);
          setRefreshRun((current) => {
            const fallbackExpected = enabledCountRef.current > 0 ? enabledCountRef.current : null;
            const expectedBase = current.expectedCount ?? fallbackExpected;
            const expectedCount =
              expectedBase === null ? null : Math.max(expectedBase - missing.length, 0);
            return {
              ...current,
              expectedCount,
              missingMarketplaces: missing,
              detailLabel: runningDetail(
                current.completedMarketplaces.length + current.failedMarketplaces.length,
                expectedCount,
                missing.length,
                current.failedMarketplaces.length,
              ),
              updatedAt: event.occurred_at ?? new Date().toISOString(),
            };
          });
          return;
        }

        if (event.kind === "refresh_completed") {
          const marketplaceId = asString(event.payload?.marketplace);
          if (!marketplaceId) return;
          setRefreshRun((current) => {
            const completedMarketplaces = uniqueStrings([
              ...current.completedMarketplaces,
              marketplaceId,
            ]);
            const processedCount = completedMarketplaces.length + current.failedMarketplaces.length;
            if (current.expectedCount !== null && processedCount >= current.expectedCount) {
              const hasWarnings =
                current.failedMarketplaces.length > 0 || current.missingMarketplaces.length > 0;
              const nextState = {
                ...INITIAL_REFRESH_RUN,
                tone: hasWarnings ? "warning" : "success",
                statusLabel: hasWarnings
                  ? "Aggiornamento completato con avvisi"
                  : "Aggiornamento completato",
                detailLabel: completedDetail(
                  completedMarketplaces.length,
                  current.failedMarketplaces.length,
                  current.missingMarketplaces.length,
                  event.occurred_at,
                ),
                expectedCount: current.expectedCount,
                completedMarketplaces,
                failedMarketplaces: current.failedMarketplaces,
                missingMarketplaces: current.missingMarketplaces,
                updatedAt: event.occurred_at ?? current.updatedAt,
              } satisfies RefreshRunState;
              invalidateRefreshQueries(queryClient);
              return nextState;
            }
            return {
              ...current,
              isBusy: true,
              tone: "running",
              statusLabel: "Aggiornamento in corso",
              completedMarketplaces,
              detailLabel: runningDetail(
                processedCount,
                current.expectedCount,
                current.missingMarketplaces.length,
                current.failedMarketplaces.length,
              ),
              currentMarketplaceId:
                current.currentMarketplaceId === marketplaceId
                  ? null
                  : current.currentMarketplaceId,
              updatedAt: event.occurred_at ?? current.updatedAt,
            };
          });
          return;
        }

        if (event.kind === "refresh_failed") {
          const marketplaceId = asString(event.payload?.marketplace);
          setRefreshRun((current) => {
            const failedMarketplaces = marketplaceId
              ? uniqueStrings([...current.failedMarketplaces, marketplaceId])
              : current.failedMarketplaces;
            const processedCount = current.completedMarketplaces.length + failedMarketplaces.length;
            if (current.expectedCount !== null && processedCount >= current.expectedCount) {
              const nextState = {
                ...INITIAL_REFRESH_RUN,
                tone: "warning",
                statusLabel: "Aggiornamento completato con avvisi",
                detailLabel: completedDetail(
                  current.completedMarketplaces.length,
                  failedMarketplaces.length,
                  current.missingMarketplaces.length,
                  event.occurred_at,
                ),
                expectedCount: current.expectedCount,
                completedMarketplaces: current.completedMarketplaces,
                failedMarketplaces,
                missingMarketplaces: current.missingMarketplaces,
                updatedAt: event.occurred_at ?? current.updatedAt,
              } satisfies RefreshRunState;
              invalidateRefreshQueries(queryClient);
              return nextState;
            }
            return {
              ...current,
              isBusy: true,
              tone: "running",
              statusLabel: "Aggiornamento in corso",
              failedMarketplaces,
              detailLabel: runningDetail(
                processedCount,
                current.expectedCount,
                current.missingMarketplaces.length,
                failedMarketplaces.length,
              ),
              currentMarketplaceId:
                current.currentMarketplaceId === marketplaceId
                  ? null
                  : current.currentMarketplaceId,
              updatedAt: event.occurred_at ?? current.updatedAt,
            };
          });
          return;
        }

        if (event.kind === "refresh_skipped") {
          const missing = asStringArray(event.payload?.missing);
          setRefreshRun({
            ...INITIAL_REFRESH_RUN,
            tone: "warning",
            statusLabel: "Refresh saltato",
            detailLabel:
              missing.length > 0
                ? `${missing.length} credenziali mancanti`
                : "Credenziali da completare prima del refresh",
            missingMarketplaces: missing,
            updatedAt: event.occurred_at ?? new Date().toISOString(),
          });
          return;
        }

        if (event.kind === "refresh_job_completed") {
          const completedMarketplaces = asStringArray(event.payload?.completed_marketplaces);
          const failedMarketplaces = asStringArray(event.payload?.failed_marketplaces);
          const missingMarketplaces = asStringArray(event.payload?.missing_marketplaces);
          const hasWarnings = failedMarketplaces.length > 0 || missingMarketplaces.length > 0;
          setRefreshRun({
            ...INITIAL_REFRESH_RUN,
            tone: hasWarnings ? "warning" : "success",
            statusLabel: hasWarnings
              ? "Aggiornamento completato con avvisi"
              : "Aggiornamento completato",
            detailLabel: completedDetail(
              completedMarketplaces.length,
              failedMarketplaces.length,
              missingMarketplaces.length,
              event.occurred_at,
            ),
            expectedCount:
              completedMarketplaces.length + failedMarketplaces.length + missingMarketplaces.length,
            completedMarketplaces,
            failedMarketplaces,
            missingMarketplaces,
            updatedAt: event.occurred_at ?? new Date().toISOString(),
          });
          invalidateRefreshQueries(queryClient);
        }

        // Catalog sync da Poleepo: 4 eventi (started/progress/
        // completed/failed) -> progress bar banner in alto.
        if (event.kind === "catalog_sync_started") {
          const totalEstimate = asNumber(event.payload?.total_estimate);
          setCatalogSync({
            ...INITIAL_CATALOG_SYNC,
            phase: "running",
            totalEstimate: totalEstimate ?? null,
            message: "Importazione catalogo Poleepo in corso…",
          });
          return;
        }

        if (event.kind === "catalog_sync_progress") {
          const processed = asNumber(event.payload?.processed) ?? 0;
          const inserted = asNumber(event.payload?.inserted) ?? 0;
          const updated = asNumber(event.payload?.updated) ?? 0;
          const pages = asNumber(event.payload?.pages) ?? 0;
          const totalEstimate = asNumber(event.payload?.total_estimate);
          const percent = asNumber(event.payload?.percent) ?? 0;
          setCatalogSync((current) => ({
            ...current,
            phase: "running",
            processed,
            inserted,
            updated,
            pages,
            totalEstimate: totalEstimate ?? current.totalEstimate,
            percent: clampPercent(percent),
            message: `Importazione catalogo Poleepo… ${processed.toLocaleString("it-IT")} prodotti`,
            hideAt: null,
          }));
          return;
        }

        if (event.kind === "catalog_sync_completed") {
          const processed = asNumber(event.payload?.processed) ?? 0;
          const inserted = asNumber(event.payload?.inserted) ?? 0;
          const updated = asNumber(event.payload?.updated) ?? 0;
          const success = event.payload?.success !== false;
          setCatalogSync({
            ...INITIAL_CATALOG_SYNC,
            phase: success ? "completed" : "failed",
            processed,
            inserted,
            updated,
            pages: asNumber(event.payload?.pages) ?? 0,
            totalEstimate: asNumber(event.payload?.total_estimate) ?? null,
            percent: 100,
            message: success
              ? `Catalogo importato: ${inserted.toLocaleString("it-IT")} nuovi, ${updated.toLocaleString("it-IT")} aggiornati`
              : "Import catalogo con errori — vedi Storico",
            hideAt: Date.now() + 5000,
          });
          // Invalida la query products per ricaricare la tabella con i
          // nuovi SKU.
          queryClient.invalidateQueries({ queryKey: ["products"] });
          return;
        }

        if (event.kind === "catalog_sync_failed") {
          setCatalogSync({
            ...INITIAL_CATALOG_SYNC,
            phase: "failed",
            percent: 0,
            message: "Import catalogo Poleepo fallito — vedi Storico",
            hideAt: Date.now() + 5000,
          });
          return;
        }
      };
      socket.onerror = () => {
        socket?.close();
      };
      socket.onclose = () => {
        if (disposed) return;
        retryTimer = window.setTimeout(connect, 1500);
      };
    };

    connect();
    return () => {
      disposed = true;
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer);
      }
      socket?.close();
    };
  }, [queryClient]);

  useEffect(() => {
    if (!refreshRun.isBusy) return;
    const WATCHDOG_MS = 3 * 60 * 1000;
    const timer = window.setTimeout(() => {
      setRefreshRun({
        ...INITIAL_REFRESH_RUN,
        tone: "warning",
        statusLabel: "Timeout - nessuna risposta dal server",
        detailLabel: "Controlla i log o riprova",
        updatedAt: new Date().toISOString(),
      });
      invalidateRefreshQueries(queryClient);
    }, WATCHDOG_MS);
    return () => window.clearTimeout(timer);
  }, [refreshRun.isBusy, refreshRun.updatedAt, queryClient]);

  const refreshControl = useMemo<LayoutOutletContext["refreshControl"]>(
    () => ({
      trigger: () => refresh.mutate(),
      isBusy: refresh.isPending || refreshRun.isBusy,
      buttonLabel: refresh.isPending || refreshRun.isBusy ? "Aggiornamento..." : "Aggiorna ora",
      statusLabel: refreshRun.statusLabel,
      detailLabel: refreshRun.detailLabel,
      tone: refreshRun.tone,
    }),
    [refresh.isPending, refreshRun],
  );

  const lastUpdateLabel = useMemo(
    () => formatLastUpdate(refreshRun.updatedAt),
    [refreshRun.updatedAt],
  );

  const badgeFor = (item: NavItem): NavItem["badge"] =>
    item.to === APPROVALS_PATH ? (unseenAlerts > 0 ? unseenAlerts : null) : item.badge;

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-paper text-foreground">
      {alertToast ? (
        <div
          className="fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-[var(--radius)] border border-line-2 bg-white px-4 py-3 shadow-popover"
          role="status"
        >
          <Bell className="h-4 w-4 text-ink" strokeWidth={1.7} aria-hidden="true" />
          <span className="text-sm text-foreground">
            Nuovo alert prezzo{alertToast.sku ? `: ${alertToast.sku}` : ""}
          </span>
          <NavLink
            to={APPROVALS_PATH}
            onClick={() => setAlertToast(null)}
            className="text-sm font-semibold text-primary underline"
          >
            Da approvare
          </NavLink>
        </div>
      ) : null}
      {/* Menu stretto come l'Order Manager: icona sopra, etichetta piccola sotto.
          Al telefono sparisce e la navigazione passa alla barra in basso. */}
      <aside
        className="flex w-[80px] shrink-0 flex-col items-center gap-1 overflow-y-auto bg-rail py-3.5 text-rail-text [scrollbar-width:none] max-md:hidden"
        aria-label="Menu"
      >
        <div className="mb-4" title="Analisi Marketplace · prezzi e margini BS COM">
          <AmLogo size={40} />
        </div>

        <nav className="flex flex-col items-center gap-1" aria-label="Navigazione principale">
          {NAV.map((item) => (
            <RailLink key={item.to} item={item} badge={badgeFor(item)} />
          ))}
        </nav>

        <div className="flex-1" />

        <div
          className="mb-1 flex flex-col items-center gap-1 text-center"
          title={meQuery.data?.display_name ?? "Utente"}
        >
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.1] text-micro font-semibold text-white">
            {initials}
          </div>
          <div className="w-16 text-center">
            <BuildBadge />
          </div>
        </div>
        <button
          type="button"
          title="Esci"
          aria-label="Esci"
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
          className="flex w-[68px] flex-col items-center gap-1 rounded-md px-1 py-2 text-[10px] text-rail-text transition-colors hover:bg-white/[0.06] hover:text-white disabled:opacity-50"
        >
          <LogOut className="h-5 w-5" strokeWidth={1.6} aria-hidden="true" />
          Esci
        </button>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex items-center gap-4 border-b border-line bg-paper px-4 py-2.5 md:px-7">
          <div className="min-w-0 flex-1">
            <MarketplaceSyncBar refreshRun={refreshRun} />
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {!refreshControl.isBusy && lastUpdateLabel && lastUpdateLabel !== "—" && (
              <span className="hidden whitespace-nowrap text-label text-ink-3 xl:inline">
                Ultimo giro alle <span className="font-mono text-ink-2">{lastUpdateLabel}</span>
              </span>
            )}
            <button
              type="button"
              onClick={refreshControl.trigger}
              disabled={refreshControl.isBusy}
              title={`Forza refresh prezzi su tutti i marketplace · ⌘R${
                refreshControl.detailLabel ? " — " + refreshControl.detailLabel : ""
              }`}
              aria-label={refreshControl.buttonLabel}
              className={cn("btn btn-sm", refreshControl.isBusy ? "btn-outline" : "btn-accent")}
            >
              <RefreshCw
                className={cn("ic", refreshControl.isBusy && "animate-spin")}
                strokeWidth={1.8}
                aria-hidden="true"
              />
              <span>{refreshControl.isBusy ? "Aggiornamento…" : "Aggiorna ora"}</span>
            </button>
            <span className="sr-only" aria-live="polite">
              {refreshControl.statusLabel ? `${refreshControl.statusLabel}` : ""}
              {refreshControl.detailLabel ? ` · ${refreshControl.detailLabel}` : ""}
            </span>
          </div>
        </header>

        {catalogSync.phase !== null && <CatalogSyncBanner state={catalogSync} />}

        <main className="scroll-thin flex-1 overflow-auto">
          <ErrorBoundary area="contenuto">
            <Outlet context={{ refreshControl }} />
          </ErrorBoundary>
        </main>
        <nav
          className="flex shrink-0 items-stretch justify-around border-t border-white/[0.08] bg-rail px-1 pb-[env(safe-area-inset-bottom)] md:hidden"
          aria-label="Navigazione telefono"
        >
          {NAV.map((item) => (
            <RailLink key={item.to} item={item} badge={badgeFor(item)} compact />
          ))}
        </nav>
      </div>
    </div>
  );
}

/** Voce del menu: icona sopra, etichetta sotto, barra ottanio a sinistra se attiva. */
function RailLink({
  item,
  badge,
  compact = false,
}: {
  item: NavItem;
  badge: NavItem["badge"];
  compact?: boolean;
}): JSX.Element {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={item.label}
      aria-label={item.label}
      className={({ isActive }) =>
        cn(
          "relative flex flex-col items-center gap-1 rounded-md px-1 py-2 text-center text-[10px] leading-tight transition-colors",
          compact ? "min-w-0 flex-1 px-0 tracking-tight" : "w-[68px]",
          isActive
            ? "bg-white/[0.08] font-medium text-white"
            : "text-rail-text hover:bg-white/[0.06] hover:text-white",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && !compact ? (
            <span
              className="absolute -left-1.5 bottom-2 top-2 w-[2px] rounded-r bg-[hsl(189_70%_42%)]"
              aria-hidden="true"
            />
          ) : null}
          <span className="relative">
            <Icon className="h-5 w-5" strokeWidth={1.6} aria-hidden="true" />
            {badge ? (
              <span className="absolute -right-2.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-white">
                {badge}
              </span>
            ) : null}
          </span>
          <span className="max-w-full truncate">{item.shortLabel ?? item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function BuildBadge(): JSX.Element | null {
  // Numero progressivo della build (count commit) - cresce di 1 ad
  // ogni commit. Davide vede subito "build 67" -> "build 68" dopo un
  // fix per verificare che l'app sta servendo il codice nuovo.
  const health = useQuery({
    queryKey: ["health"],
    queryFn: api.health,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  if (!health.data) return null;
  const {
    version,
    build_number: buildNumber,
    build_sha: buildSha,
    build_dirty: dirty,
    started_at: startedAt,
  } = health.data;
  const title = [
    `Versione ${version}`,
    `build #${buildNumber}`,
    buildSha && buildSha !== "unknown" ? `commit ${buildSha}` : null,
    dirty ? "lavoro non committato" : null,
    `avviata ${startedAt}`,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <span
      className="block truncate font-mono text-[10px] text-rail-text"
      title={title}
      aria-label={title}
    >
      build {buildNumber}
      {dirty ? "*" : ""}
    </span>
  );
}

function formatLastUpdate(updatedAt: string | null): string {
  if (!updatedAt) return "—";
  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

type SyncDotState = "updating" | "error" | "ok" | "stale" | "never";

function MarketplaceSyncBar({ refreshRun }: { refreshRun: RefreshRunState }): JSX.Element | null {
  // 4 stati colore (richiesta utente):
  //   azzurro = in aggiornamento ora
  //   rosso   = errore ultimo refresh
  //   verde   = OK (snapshot fresh entro 6h o appena completato)
  //   arancione = in attesa (snapshot stale o mai sincronizzato)
  const status = useQuery({
    queryKey: ["marketplace-sync-status"],
    queryFn: api.marketplaceSyncStatus,
    refetchInterval: 60_000,
    refetchOnMount: "always",
  });

  if (!Array.isArray(status.data) || status.data.length === 0) {
    return (
      <div className="sync-row" aria-label="Sync marketplace">
        <span className="text-label font-semibold text-ink-2">Canali</span>
        <span className="text-label text-ink-3">stato non disponibile</span>
      </div>
    );
  }

  const failed = new Set(refreshRun.failedMarketplaces);
  const completed = new Set(refreshRun.completedMarketplaces);

  return (
    <div
      className="sync-row"
      role="group"
      aria-label="Stato sincronizzazione marketplace"
    >
      <span className="hidden shrink-0 text-label font-semibold text-ink-2 sm:inline">Canali</span>
      <ul className="flex min-w-0 items-center gap-x-1.5 gap-y-0.5 overflow-x-auto md:flex-wrap md:overflow-visible">
        {status.data.map((row) => {
          const state = resolveSyncState({
            row,
            currentId: refreshRun.currentMarketplaceId,
            failed,
            completed,
          });
          const meta = SYNC_DOT_META[state];
          const lastLabel = row.last_sync_at
            ? new Date(row.last_sync_at).toLocaleString("it-IT", {
                dateStyle: "short",
                timeStyle: "short",
              })
            : "mai sincronizzato";
          // Orario inline (HH:MM) accanto alla sigla market: cosi' di
          // colpo Davide vede "MDM 15:14 · LRY IT 14:22" e capisce
          // quale market e' indietro.
          const timeOnly = row.last_sync_at
            ? new Date(row.last_sync_at).toLocaleTimeString("it-IT", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "—:—";
          return (
            <li
              key={row.marketplace_id}
              className={cn(
                "flex h-6 items-center gap-1.5 rounded-md border px-1.5",
                state === "error" ? "border-danger/50 bg-card" : "border-transparent",
              )}
              title={`${row.label} · ${meta.label} · ${lastLabel}`}
              aria-label={`${row.label}: ${meta.label} ultimo refresh ${lastLabel}`}
            >
              <span className={cn("dot", meta.cls)} aria-hidden="true" />
              <span className="whitespace-nowrap font-mono text-micro font-medium text-ink">
                {shortMarketplaceLabel(row.marketplace_id, row.country)}
              </span>
              <span
                className={cn(
                  "font-mono text-micro tabular-nums",
                  state === "error" ? "font-semibold text-danger" : "text-ink-3",
                )}
              >
                {timeOnly}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const SYNC_DOT_META: Record<SyncDotState, { cls: string; label: string }> = {
  updating: { cls: "dot-info dot-pulse", label: "in aggiornamento" },
  error: { cls: "dot-err", label: "errore" },
  ok: { cls: "dot-ok", label: "OK" },
  stale: { cls: "dot-warn", label: "in attesa" },
  never: { cls: "dot-warn", label: "mai sincronizzato" },
};

function resolveSyncState({
  row,
  currentId,
  failed,
  completed,
}: {
  row: { marketplace_id: string; last_sync_at: string | null; fresh: boolean };
  currentId: string | null;
  failed: Set<string>;
  completed: Set<string>;
}): SyncDotState {
  if (currentId === row.marketplace_id) return "updating";
  if (failed.has(row.marketplace_id)) return "error";
  if (completed.has(row.marketplace_id)) return "ok";
  if (!row.last_sync_at) return "never";
  return row.fresh ? "ok" : "stale";
}

export function shortMarketplaceLabel(id: string, country: string): string {
  // 3-4 char per stare compatti: prefisso + country.
  const prefix = id.startsWith("maisons_du_monde")
    ? "MDM"
    : id.startsWith("leroy_merlin")
      ? "LRY"
      : id.startsWith("vente_unique")
        ? "VU"
        : id.startsWith("amazon")
          ? "AMZ"
          : id.startsWith("bricobravo")
            ? "BB"
            : id.slice(0, 3).toUpperCase();
  return `${prefix} ${country}`;
}

function startRefreshRun(expectedCount: number | null): RefreshRunState {
  return {
    ...INITIAL_REFRESH_RUN,
    isBusy: true,
    tone: "running",
    statusLabel: "Aggiornamento in corso",
    detailLabel: runningDetail(0, expectedCount, 0, 0),
    expectedCount,
    updatedAt: new Date().toISOString(),
  };
}

function runningDetail(
  processedCount: number,
  expectedCount: number | null,
  missingCount: number,
  failedCount: number,
): string {
  const parts: string[] = [];
  if (expectedCount && expectedCount > 0) {
    parts.push(`${processedCount}/${expectedCount} marketplace completati`);
  } else {
    parts.push("Sincronizzazione prezzi e buybox in corso");
  }
  if (missingCount > 0) {
    parts.push(`${missingCount} da configurare`);
  }
  if (failedCount > 0) {
    parts.push(`${failedCount} con errore`);
  }
  return parts.join(" · ");
}

function completedDetail(
  completedCount: number,
  failedCount: number,
  missingCount: number,
  occurredAt?: string,
): string {
  const parts: string[] = [];
  parts.push(`${completedCount} marketplace aggiornati`);
  if (failedCount > 0) {
    parts.push(`${failedCount} con errore`);
  }
  if (missingCount > 0) {
    parts.push(`${missingCount} da configurare`);
  }
  if (occurredAt) {
    parts.push(`alle ${formatTime(occurredAt)}`);
  }
  return parts.join(" · ");
}

function marketplaceProgressDetail(
  marketplaceId: string | null,
  productsSeen: number | null,
  labelsById: Record<string, string>,
): string {
  const marketplace = marketplaceId ? (labelsById[marketplaceId] ?? marketplaceId) : "Marketplace";
  const suffix =
    productsSeen !== null && productsSeen > 0
      ? `: lettura offerte per ${productsSeen} prodotti`
      : ": lettura offerte";
  return `${marketplace}${suffix}`;
}

function heartbeatDetail(
  marketplaceId: string | null,
  eans: number | null,
  labelsById: Record<string, string>,
): string {
  const marketplace = marketplaceId ? (labelsById[marketplaceId] ?? marketplaceId) : "Marketplace";
  const suffix =
    eans !== null && eans > 0 ? `: server attivo, ${eans} EAN in elaborazione` : ": server attivo";
  return `${marketplace}${suffix}`;
}

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--:--";
  return date.toLocaleTimeString("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function getEventsSocketUrl(): string {
  if (typeof window === "undefined") return "ws://127.0.0.1:8765/ws/events";
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  // Dev: SPA su :5173 (Vite) con proxy a backend :8765. Useremo direttamente
  // il backend per il WebSocket (Vite proxy WS richiede config esplicita).
  if (window.location.port === "5173") {
    const hostname = window.location.hostname || "127.0.0.1";
    return `${protocol}://${hostname}:8765/ws/events`;
  }
  // Prod: stessa origine via Caddy reverse_proxy /ws/*. Usa `location.host`
  // (include port solo se non standard) → niente trailing colon su :443/:80.
  const host = window.location.host || "127.0.0.1:8765";
  return `${protocol}://${host}/ws/events`;
}

function parseSocketEvent(raw: string): RefreshSocketEvent | null {
  try {
    return JSON.parse(raw) as RefreshSocketEvent;
  } catch {
    return null;
  }
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value ? value : null;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.length > 0)
    : [];
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  if (value > 100) return 100;
  return Math.round(value);
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values));
}

function invalidateRefreshQueries(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: ["products"] });
  queryClient.invalidateQueries({ queryKey: ["approvals"] });
  queryClient.invalidateQueries({ queryKey: ["marketplace-sync-status"] });
}

interface CatalogSyncBannerProps {
  state: CatalogSyncState;
}

/**
 * Banner sotto l'header con progress bar per l'import catalogo
 * Poleepo. Si mostra quando arriva `catalog_sync_started`, aggiorna
 * la percentuale ad ogni `catalog_sync_progress`, e si nasconde
 * automaticamente 5s dopo `catalog_sync_completed/_failed`.
 *
 * La percentuale e' una stima (vedi `_compute_percent` nel job
 * builder backend). E' clamped a 95% durante il sync e raggiunge
 * 100% solo all'evento `_completed` per evitare la situazione
 * imbarazzante in cui la barra si ferma a 100% mentre il sync sta
 * ancora lavorando (catalogo cresciuto dall'ultima stima).
 */
function CatalogSyncBanner({ state }: CatalogSyncBannerProps): JSX.Element {
  const isFailure = state.phase === "failed";
  const tone = isFailure
    ? "border-destructive/30 bg-destructive/5 text-destructive"
    : state.phase === "completed"
      ? "border-accent/40 bg-accent/10 text-accent"
      : "border-info/30 bg-info/5 text-info";

  const fillTone = isFailure
    ? "bg-destructive"
    : state.phase === "completed"
      ? "bg-accent"
      : "bg-info";

  const counterLabel = state.totalEstimate
    ? `${state.processed.toLocaleString("it-IT")} / ~${state.totalEstimate.toLocaleString("it-IT")}`
    : state.processed.toLocaleString("it-IT");

  return (
    <div role="status" aria-live="polite" className={`border-b px-7 py-2 ${tone}`}>
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="font-medium">{state.message}</span>
        <span className="font-mono tabular-nums">
          {counterLabel} · {state.percent}%
        </span>
      </div>
      <div className="mt-1.5 h-1 w-full overflow-hidden rounded bg-ink-4/15">
        <div
          className={`h-full rounded transition-all duration-300 ease-out ${fillTone}`}
          style={{ width: `${Math.max(2, state.percent)}%` }}
        />
      </div>
    </div>
  );
}
